import React, { useEffect, useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import DiagramService from "../../../../../services/diagrams/DiagramService";
import { DiagramTrainingsFrequency } from "../../../../../types/types";
import { withTranslation } from "react-i18next";
import Spinner from "../../../../../common/Spinner";

interface Props {
    workbookId: number;
    t: any;
}

const DiagramTrainingFrequency: React.FC<Props> = ({ workbookId, t }) => {
    const [rawData, setRawData] = useState<DiagramTrainingsFrequency[]>([]);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        setLoading(true);

        DiagramService.getTrainingsFrequency(workbookId)
            .then((response) => {
                const result: DiagramTrainingsFrequency[] = response.data;

                const years = result.map(r =>
                    new Date(r.trainingDate).getFullYear()
                );
                const newestYear = years.length ? Math.max(...years) : null;

                setRawData(result);
                setSelectedYear(newestYear);
            })
            .catch((error) => {
                console.error("Error loading training frequency data:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [workbookId]);

    const availableYears = useMemo(() => {
        const years = new Set<number>();
        rawData.forEach(item => {
            years.add(new Date(item.trainingDate).getFullYear());
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [rawData]);

    const filteredData = useMemo(() => {
        if (!selectedYear) return [];
        return rawData.filter(
            item => new Date(item.trainingDate).getFullYear() === selectedYear
        );
    }, [rawData, selectedYear]);

    const chartData = useMemo(() => {
        if (!selectedYear || filteredData.length === 0) return [];

        return [
            [
                { type: "date", id: "Date" },
                { type: "number", id: "Count" },
            ],
            ...filteredData.map(entry => [
                new Date(entry.trainingDate),
                entry.count,
            ]),
        ];
    }, [filteredData, selectedYear]);

    const options = {
        animation: {
            startup: true,
            duration: 2000
        },
        title: `${t("diagram.training_attendance")} (${selectedYear})`,
        titleTextStyle: { color: "#8e7f90" },
        colorAxis: {
            colors: ["#800080", "#200080"],
        },
        calendar: {
            cellSize: 12.5,
            yearLabel: {
                fontSize: 12,
                color: "#8e7f90",
                bold: false,
            },
            monthOutlineColor: {
                stroke: "#800080",
                strokeOpacity: 1,
                strokeWidth: 1
            },
            monthLabel: {
                color: "#8e7f90",
            }
        },
        backgroundColor: "transparent",
    };

    if (loading) return <Spinner />;
    if (!selectedYear || chartData.length <= 1) return null;

    return (
        <div
            style={{
                overflowX: "auto",
                overflowY: "hidden",
                marginBottom: "2rem",
            }}
        >
            {/* YEAR SELECT */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "1rem",
                }}
            >
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {availableYears.map(year => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            {/* CHART */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Chart
                    chartType="Calendar"
                    width="700px"
                    data={chartData}
                    options={options}
                />
            </div>
        </div>
    );
};

export default withTranslation("global")(DiagramTrainingFrequency);
